import React, { useState, useEffect } from 'react';
import * as S from './StickyAction.style';
import { useStyles } from '@archipro-design/aria';
import { useScroll } from 'ahooks';

export interface Position {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
}

interface ThresholdRef {
    ref: React.RefObject<Element>;
    threshold: number;
}

interface StickyActionProps {
    position: Position;
    width?: number;
    height?: number;
    usePxValues?: boolean;
    revealBounds?: number[];
    hiddenOn?: ThresholdRef[];
}

const StickyAction: React.FC<React.PropsWithChildren<StickyActionProps>> = ({
    position,
    width,
    height = 60,
    usePxValues,
    revealBounds,
    hiddenOn,
    children,
}) => {
    const [visibilityChecks, setVisibilityChecks] = useState<Element[]>([]);
    const [reveal, setReveal] = useState(!!revealBounds);

    const { css } = useStyles({
        position,
        width: width,
        height: height,
        usePxValues: !!usePxValues,
        visible: reveal && visibilityChecks.length === 0,
    });
    const scroll = useScroll();

    useEffect(() => {
        if (hiddenOn) {
            const thresholds = hiddenOn.map((ref) => ref.threshold);
            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        const threshold = hiddenOn.find(
                            (r) => r.ref.current === entry.target,
                        )?.threshold;
                        if (
                            entry.isIntersecting &&
                            typeof threshold === 'number' &&
                            entry.intersectionRatio >= threshold
                        ) {
                            setVisibilityChecks((prevChecks) => {
                                if (!prevChecks.includes(entry.target)) {
                                    return [...prevChecks, entry.target];
                                }
                                return prevChecks;
                            });
                        } else {
                            setVisibilityChecks((prevChecks) => {
                                return prevChecks.filter(
                                    (target) => target !== entry.target,
                                );
                            });
                        }
                    });
                },
                { threshold: thresholds },
            );

            hiddenOn.forEach((thresholdRef) => {
                if (thresholdRef.ref.current) {
                    observer.observe(thresholdRef.ref.current);
                }
            });

            return () => {
                hiddenOn.forEach((thresholdRef) => {
                    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
                    if (thresholdRef.ref.current) {
                        // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
                        observer.unobserve(thresholdRef.ref.current);
                    }
                });
            };
        }
    }, [hiddenOn]);

    useEffect(() => {
        if (scroll && revealBounds && revealBounds.length > 0) {
            const topBound = revealBounds[0] ?? 0;
            const bottomBound = revealBounds[1] ?? null;
            let withinBounds = scroll.top >= topBound;
            if (bottomBound && bottomBound > topBound) {
                withinBounds = withinBounds && scroll.top < bottomBound;
            }
            setReveal(withinBounds);
        }
    }, [revealBounds, scroll]);

    return <div className={css(S.StickyActionContainer)}>{children}</div>;
};

export default StickyAction;
